import _ from "underscore"
import URI from 'urijs'
import {DEFAULT_TIME_ZONE_GROUP_ID, USERS_WITHOUT_GROUP_ID} from 'constants'
import GroupType from './enums/GroupType'

/**
 * Converts array of objects to set ([1, 5, 7] -> {1: true, 5: true, 7: true}.
 * @param {Array} array Array of objects to convert.
 */
export function arrayToSet(array)
{
    const set = {};
    array.forEach(item => set[item] = true);
    return set;
}

export function arrayToMap(array, keyMapper = (item) => item.id)
{
    const map = {};
    array.forEach(item => map[keyMapper(item)] = item);
    return map;
}

/**
 * Take set ({1: true, 2: false, 3: false}) and filter its keys by predicate
 */
export const filterSet = (set, predicate) => {
    return Object.keys(set).filter(predicate).reduce((res, key) => Object.assign(res, {[key]: set[key]}), {})
}

export const getDiffOfSets = (before, after) => {
    const added = {}
    const removed = {}

    Object.keys(before).forEach(key => {
        if (before[key] && !after[key])
            removed[key] = true
    })
    Object.keys(after).forEach(key => {
        if (after[key] && !before[key])
            added[key] = true
    })

    return {
        added,
        removed
    }
}

/**
 * Move item after specified item in array. Use only with primitives.
 * @param {Array} array Array of primitives.
 * @param item Item to move.
 * @param insertAfterItem Item, after witch insert. If null or it not exist in array, insert at start
 */
export function changeItemPosition(array, item, insertAfterItem)
{
    const result = [...array]
    result.splice(array.indexOf(item), 1)
    const insertIndex = insertAfterItem === null ? 0 : result.indexOf(insertAfterItem) + 1
    result.splice(insertIndex, 0, item)
    return result;
}

export function areUnorderedArraysEqual(a1, a2)
{
    if (a1.length !== a2.length)
        return false;
    const s1 = arrayToSet(a1);
    return a2.every(item => s1[item]);
}

export function unorderedArraysDifference(before, after) {
    const beforeSet = arrayToSet(before);
    const afterSet = arrayToSet(after);

    const added = [];
    after.forEach(item => {
        if (!beforeSet[item]) {
            added.push(item)
        }
    })

    const removed = [];
    before.forEach(item => {
        if (!afterSet[item]) {
            removed.push(item)
        }
    })

    return {added, removed}
}

export function isArraysItemsOrderEqual(a1, a2)
{
    return a1.length === a2.length && a1.every((item, index) => item === a2[index])
}

export function isArraysWithIdsItemsOrderEqual(a1, a2)
{
    return a1.length === a2.length && a1.every((item, index) => item.id === a2[index].id)
}

export function isMapsWithIdsItemsOrderEqual(a1, a2)
{
    return _.size(a1) === _.size(a2) && Object.keys(a1).every((item) => a2.hasOwnProperty(item))
}

export const isOrderFullyExistsAtTheBeginningOf = (array, tested, compFn = (el1, el2) => el1.id === el2.id) => {
    return tested.length >= array.length && array.every((item, index) => compFn(item, tested[index]))
}

/**
 * Escapes &amp;, &gt;, &lt; and &quot; symbols to display str on a page.
 * Returns escaped string.
 */
export function escapeHtmlSymbols(str)
{
    if ((str !== undefined) && (typeof str === "string"))
    {
        return str
                .replace(/&/g, '&amp;')
                .replace(/>/g, '&gt;')
                .replace(/</g, '&lt;')
                .replace(/"/g, '&quot;');
    }

    return "";
}

/**
 * Opposite to escapeHtmlSymbols
 * Replaces &amp;, &gt;, &lt; and &quot; with corresponding characters.
 * Returns new string.
 */
export function unescapeHtmlSymbols(str)
{
    if ((str !== undefined) && (typeof str === "string"))
    {
        return str
                .replace(/&amp;/g, '&')
                .replace(/&gt;/g, '>')
                .replace(/&lt;/g, '<')
                .replace(/&quot;/g, '"')
                .replace(/&#39;/g, "'");
    }
    else
    {
        return "";
    }
}

/**
 * Returns a concatenation of two passed strings. If any string is NULL,
 * the other one will be return. Returns NULL if both strings are NULL
 * @param stringA
 * @param stringB
 * @param delimiter
 */
export function concatStr(stringA, stringB, delimiter)
{
    const del = delimiter ? delimiter : "";
    if (stringB)
        stringA = stringA ? stringA + del + stringB : stringB;
    return stringA;
}

/**
 * Returns <code>maxLength</code> first symbols of <code>string</code>. If length of <code>string</code> is less then
 * <code>maxLength</code>, whole <code>string</code> will be returned.
 *
 * @param string the string to truncate
 * @param maxLength maximum length of returning string
 * @param ignoreSuffixLength specifies whether suffix should be assumed as a part of returning string. If false, then
 *        returning string will contains n = (maxLength - passedSuffix.length) first symbols of <code>string</code>
 *        and suffix. If true, then n = maxLength.
 *        [Optional] default : true
 * @param passedSuffix the string to be appended to returning string if string's length greater then maxLength.
 *        [Optional] default : "..."
 */
export function truncateStringByLength(string, maxLength, ignoreSuffixLength, passedSuffix)
{
    const suffix = passedSuffix ? passedSuffix : "...";
    const resultLength = ignoreSuffixLength ? maxLength : maxLength - suffix.length;

    if (string.length > maxLength)
        return string.substring(0, resultLength) + suffix;
    else
        return string;
}

/**
 * Compare two strings in case-insensitive alphabetic order. If supported, uses "en-US" locale.
 * @param a first string
 * @param b second string
 */
export function stringComparator(a, b)
{
    // set "en-US" locale for compare strings (it works only in modern browsers expect "safari") see bug #54120
    return a.toLocaleLowerCase().localeCompare(b.toLocaleLowerCase(), "en-US");
}

/**
 * Compare two strings in case-sensitive alphabetic order. If supported, uses "en-US" locale.
 * @param a first string
 * @param b second string
 */
export function stringComparatorCaseSensitive(a, b)
{
    // set "en-US" locale for compare strings (it works only in modern browsers expect "safari") see bug #54120
    return a.localeCompare(b, "en-US");
}

/**
 * Compare two entities with 'name' property by this property.
 * @param a first entity
 * @param b second entity
 */
export function nameComparator(a, b)
{
    return stringComparator(a.name, b.name);
}

/**
 * Compare two numbers
 * @param a first number
 * @param b second number
 */
export function numberComparator(a, b)
{
    return a - b;
}

export function userGroupsComparator(g1, g2) {
    return groupsComparator(g1, g2, USERS_WITHOUT_GROUP_ID)
}

export function timeZoneGroupsComparator(g1, g2) {
    return groupsComparator(g1, g2, DEFAULT_TIME_ZONE_GROUP_ID)
}

function groupsComparator(g1, g2, defaultGroupId) {
    if (g1.id === g2.id) return 0
    if (g1.id === defaultGroupId) return -1
    if (g2.id === defaultGroupId) return 1
    return stringComparator(g1.name, g2.name)
}

/**
 * Function trims specified value
 * @param str - if str converted to boolean is false then empty string will be returned
 * @returns {String}
 */
export function trim(str)
{
    str = String(str);
    const whitespace = ' \n\r\t\f\x0b\xa0\u2000\u2001\u2002\u2003\u2004\u2005\u2006\u2007\u2008\u2009\u200a\u200b\u2028\u2029\u3000';

    let lUk, rUk;
    for (lUk = 0; lUk < str.length; lUk++)
    {
        if (whitespace.indexOf(str.charAt(lUk)) === -1)
            break;
    }
    if (lUk === str.length) return '';

    for (rUk = str.length - 1; ; rUk--)
    {
        if (whitespace.indexOf(str.charAt(rUk)) === -1)
            break;
    }

    return str.substring(lUk, rUk + 1);
}

export function leadZero(n)
{
    if (n < 10)
        return '0' + n;
    else
        return '' + n;
}

export function formatRawSecondsOffset(rawSecondsOffset)
{
    const absMinutesOffset = Math.abs(rawSecondsOffset) / 60;
    const offsetValue = Math.round(absMinutesOffset / 60 - absMinutesOffset % 60 / 60) + ":" + leadZero(absMinutesOffset % 60);
    return rawSecondsOffset < 0 ? '-' + offsetValue : "+" + offsetValue;
}

/**
 * Make map of id -> entity and array of its order in this map by array of entities with "id" property
 */
export function makeMapAndOrderForEntitiesArray(array)
{
    const map = {};
    const order = [];
    for (let i = 0; i < array.length; i++)
    {
        const id = array[i].id;
        map[id] = array[i];
        order.push(id);
    }
    return {
        map: map,
        order: order
    }
}

/**
 * Make map of id -> entity by array of entities with "id" property
 */
export function makeMapForEntitiesArray(array)
{
    const result = {};
    for (let i = 0; i < array.length; i++)
    {
        result[array[i].id] = array[i];
    }
    return result;
}

export function isEmailValid(email)
{
    const str = email.trim();
    const withoutWhitespaces = /^\S*$/.test(str)
    return withoutWhitespaces && /.+@.+/i.test(str)
}

/**
 * Compare two dates only by "date" part, excluding "time" part
 *
 * @param date1
 * @param date2
 * @return Number : negative if date1<date2, 0 if date1 == date2, positive if date1>date2 (only date, excluding time)
 */
export function compareDates( date1, date2 )
{
    return date1.getFullYear() - date2.getFullYear() ||
           date1.getMonth() - date2.getMonth() ||
           date1.getDate() - date2.getDate();
}

export function compareNullableStrDates( date1, date2 )
{
    if (!date1 && !date2) return 0
    if (!date2) return -1
    if (!date1) return 1
    if (typeof(date1) === 'string') date1 = Date.parseJSONDate(date1)
    if (typeof(date2) === 'string') date2 = Date.parseJSONDate(date2)
    return compareDates(date1, date2)
}


/**
 * Compare two dates only by "date" part, excluding "time" part
 * @param date1
 * @param date2
 */
export function areDatesEqual(date1, date2) {
    return date1.getFullYear() === date2.getFullYear() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getDate() === date2.getDate();
}

export function promisifyWithDelay(f, delay = 500) {
    return function() {
        const args = arguments;
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                try {
                    const result = f.apply(null, args);
                    resolve(result)
                }
                catch (e) {
                    reject(e)
                }
            }, delay);
        })
    }
}
/**
 * Creates a handler that will call given function on given scope
 * @param f function
 * @param scope scope object
 */
export function makeHandler( f, scope )
{
    return function( event )
    {
        return f.call(scope, event);
    }
}


/*
 * Unifies new line symbol in the specified string. It is needed for unified string length determining in various
 * browsers, because in some browser (e.g. IE) new line symbol is '\r\n', but in others (e.g. Firefox) new line symbol
 * is '\n'. Because of these differences length of a string can vary in various browsers. To avoid differences this
 * function should be called before a string length determining.
 *
 * @param str a string where new line symbol should be unified.
 * @return string with unified new line symbol.
 */
export function getStringWithUnifiedNewLineSymbol(str) {
    return str.replace(/(\r?\n)|(\r\n?)/g, '\r\n');
}

export function isNotEmpty(str)
{
    return trim(str).length > 0;
}

export function removeContextFromProductUrl(productUrl) {
    return productUrl.substr(0, productUrl.lastIndexOf('/') + 1);
}


/**
 * Client sorting orders
 * @type {{ASC: string, DESC: string}}
 */

export const SORT_ORDER = {
    ASC: "Asc",
    DESC: "Desc"
}

/**
 * Get comparator function to sort object arrays by field values: arr.sort(comparator("name", ORDER.ASC))
 * @param field field to sort by. Should be number or strings values.
 * @param order order to sort
 * @return {function(*, *)} comparator function
 */

export function comparator(field, order) {
    return (a, b) => {
        let result = 0;
        if (a[field] < b[field]) {
            result = -1;
        } else if (a[field] > b[field]) {
            result = 1;
        }
        return (order === SORT_ORDER.ASC) ? result : -result;
    }
}

/**
 * Defers function execution, calling it after 10 ms (not 0 ms, necessary for certain browsers).
 * @param f Function to call.
 * @param context Function context (this).
 */
export function defer(f, context)
{
    if (context)
        return setTimeout(function() {
            f.apply(context)
        }, 10);
    else
        return setTimeout(f, 10);
}

export function throttle(func, ms) {
    let isThrottled = false;
    let savedArgs = null;
    let savedContext = null;

    function wrapper() {
        if (isThrottled) {
            savedArgs = arguments;
            savedContext = this;
            return;
        }
        func.apply(this, arguments);
        isThrottled = true;
        setTimeout(function() {
            isThrottled = false;
            if (savedArgs) {
                wrapper.apply(savedContext, savedArgs);
                savedArgs = null;
                savedContext = null;
            }
        }, ms);
    }

    return wrapper;
}

export function throttlePromise(func, ms) {
    let isThrottled = false
    let savedArgs = null
    let savedContext = null
    let promiseResolve
    let promiseReject

    function wrapper() {
        if (isThrottled) {
            savedArgs = arguments
            savedContext = this
            return new Promise((resolve, reject) => {
                promiseResolve = resolve
                promiseReject = reject
            })
        }
        isThrottled = true

        setTimeout(function() {
            isThrottled = false
            if (savedArgs) {
                wrapper.apply(savedContext, savedArgs).then(promiseResolve, promiseReject)
                savedArgs = null
                savedContext = null
            }
        }, ms)
        return func.apply(this, arguments)
    }

    return wrapper
}

export function filterMapByKeys(map, keys) {
    map = {...map}
    keys.forEach(key => delete map[key])
    return map
}

export function filterMapByKey(map, key) {
    return filterMapByKeys(map, [key])
}

export function filterArrayByKeys(arr, keys, keyMapper = (item) => item) {
    const keysSet = arrayToSet(keys)
    return arr.filter(item => !keysSet[keyMapper(item)])
}

export function filterMapByPredicate(map, predicate) {
    let result = {}

    _.each(map, (value, key) => {
        if (predicate(value, key)) {
            result[key] = value
        }
    })

    return result
}

export function filterArrayByArrayOfIds(array, ids) {
    const idsSet = arrayToSet(ids)
    return array.filter(item => !!idsSet[item.id])
}

export function anyInSet(set) {
    return Object.keys(set).some((element) => set[element])
}

export function isInt(num) {
    return !isNaN(num) && parseInt(num) == parseFloat(num)
}

export function isPositiveInt(num) {
    return !isNaN(num) && parseInt(num) == parseFloat(num) && parseInt(num) > 0
}

export function createArrayOfObjectFromMap(map, mapObjectFunc, keys = []) {
    let array = []

    if(keys.length === 0) {
        keys = Object.keys(map)
    }

    keys.forEach(key => {
        array.push(mapObjectFunc(key, map[key]))
    })

    return array
}

/**
 * Converts string to bytes encoded in UTF-16LE
 * @param str String to convert.
 * @returns {ArrayBuffer}
 */
function stringToUTF16(str) {
    const buffer = new ArrayBuffer(str.length * 2)
    const bufferProxy = new Uint16Array(buffer)
    for (let i = 0; i < str.length; i++)
        bufferProxy[i] = str.charCodeAt(i)
    return buffer
}

/**
 * Detects if browser supports saving files from client code.
 * @returns {boolean}
 */
export function saveFileSupported() {
    return !!(window.Blob)
}

/**
 * Saves file to local file system.
 * @param content String or byte buffer with file contents.
 * @param fileName Desired file name.
 * @param blobOptions Default implementation uses Blob for saving, here you can pass blob options such as encoding, type, etc.
 */
export function saveFile(content, fileName, blobOptions) {
    if (window.Blob) {
        const utf16 = blobOptions.encoding.toLowerCase() === 'utf-16'
        const blobData = []
        if (utf16) {
            blobData.push(new Uint8Array([0xFF, 0xFE]))
            if (_.isString(content))
                content = stringToUTF16(content)
        }
        blobData.push(content)

        const blob = new Blob(blobData, blobOptions)
        // Use FileSaver.js library
        window.saveAs(blob, fileName)
    }
}

export function toggleElements(jqueryElementsArray, toggle) {
    jqueryElementsArray.forEach($el => $el.toggle(toggle))
}

export const convertUsersFilterDependingOnFeatures = (usersFilter, ugrEnabled, tzgEnabled, availableUsers) => {
    let selectedGroups = usersFilter.groupIds

    if (selectedGroups && selectedGroups.length > 0) {
        if (!ugrEnabled && usersFilter.groupType === GroupType.USER_GROUP) {
            return convertGroupsForFilter(selectedGroups, availableUsers, usersFilter, (groupId, user) => groupId === user.userGroupId)
        } else if (!tzgEnabled && usersFilter.groupType === GroupType.TIME_ZONE_GROUP) {
            return convertGroupsForFilter(selectedGroups, availableUsers, usersFilter, (groupId, user) => groupId === user.timeZoneGroupId)
        }
    }

    return usersFilter
}

const convertGroupsForFilter = (selectedGroups, availableUsers, usersFilter, groupPredicate = (groupId, user) => {}) => {
    let userIdsFromGroups = availableUsers.filter(user =>
        selectedGroups.some(groupId => groupPredicate(groupId, user))
    ).map(user => user.id)
    return {
        ...usersFilter,
        userIds: [...usersFilter.userIds, ...userIdsFromGroups],
        groupIds: [],
        groupType: null
    }
}

/**
 * Creates absolute link with preceding base path and query params.
 */
export function createAbsoluteLink(path) {
    return URI()
        .pathname(URI.joinPaths(window.BASE_PATH, path))
        .toString()
}

export function removeHashAndQueryFromLink(path) {
    const urlObj = new URL(path)
    urlObj.hash = ''
    urlObj.search = ''

    return urlObj.toString()
}

/**
 * Get current relative link without context, query and hash. "/timetrack/enter.do"
 */
export function getCurrentRelativeLinkWithoutQuery() {
    const absoluteLink = removeHashAndQueryFromLink(createAbsoluteLink(''))
    const windowHrefWithoutQuery = removeHashAndQueryFromLink(window.location.href)

    return windowHrefWithoutQuery.substring(absoluteLink.length - 1, windowHrefWithoutQuery.length)
}



// WEBPACK FOOTER //
// ./web/js/atUtils.js